// import '@babel/polyfill';
import * as Sentry from '@sentry/vue';
import { Integration } from '@sentry/core';
import 'mutationobserver-shim';
import { createApp, ref } from 'vue'; // import a function
import App from '@/App.vue';
import { defineAsyncComponent } from 'vue'; // Allows asynchronous imports
import { createPinia } from 'pinia';
import { useAuthStore } from './stores/authStore';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { config } from '@/config';
import i18n from './i18n';
import VueMatomo from 'vue-matomo';
import userManager from './helperScripts/userManagement/userManager';
import serviceWorkerHelper from './helperScripts/pwa/serviceWorkerHelper';
import installPromptHelper from './helperScripts/pwa/installPromptHelper';
import initRouter from './router';
import { Router } from 'vue-router';
import '@/scss/__custom_bootstrap.scss';
import Toast, { POSITION } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import '@/scss/__variables.scss';
import '@/scss/baseStyle.scss';

// create a pinia instance using the create function
const pinia = createPinia();
const authStore = useAuthStore(pinia);

const keycloakOptions = {
  url: config.KEYCLOAK_URL,
  realm: config.KEYCLOAK_REALM,
  clientId: config.KEYCLOAK_CLIENT,
};

const initOptions: KeycloakInitOptions = {
  onLoad: 'login-required',
  // promiseType: "native",
};

const initializeKeycloak = async () => {
  const keycloak = new Keycloak(keycloakOptions);
  return new Promise((resolve, reject) => {
    keycloak
      .init(initOptions)
      .then((authenticated) => {
        authStore.keycloak = keycloak;
        resolve(authenticated);
      })
      .catch((error) => {
        reject(error);
      });
    keycloak.onTokenExpired = () => {
      console.log('Token expired');
      keycloak.updateToken(5).then((refreshed) => {
        console.log('Token refreshed', refreshed);
      });
    };
  });
};

//Create app
export const app = createApp(App);

const initialize = async () => {
  //Import SFC for Loading screen
  const Loading = defineAsyncComponent(() => import('@/Loading.vue'));

  //Mount Vue App with Loading screen
  const loadingApp = createApp(Loading).use(i18n);
  loadingApp.mount('#app');

  try {
    serviceWorkerHelper.updateSW(); //Update and register service worker for pwa
    installPromptHelper.addEventListenersForPwaInstallation(); //Enables custom prompt for pwa installation

    await initializeKeycloak();

    //Init user after authentication
    await userManager.initUser();

    // Unmount Loading screen
    loadingApp.unmount();
    // Mount main app
    // register plugins
    const router = initRouter();
    app.use(router).use(pinia).use(i18n);
    app.use(Toast, {
      // global defaults for all toasts, e.g. position
      position: POSITION.TOP_RIGHT,
    });
    initSentry(app, router);

    if (config.MATOMO_ENABLED === true) {
      //use matomo
      app.use(VueMatomo, {
        host: config.MATOMO_URL,
        siteId: config.MATOMO_SITE_ID,
        trackerFileName: 'matomo',
        router: router,
        enableLinkTracking: true,
        requireConsent: true,
        trackInitialView: true,
        disableCookies: false,
        enableHeartBeatTimer: false,
        heartBeatTimerInterval: 15,
        debug: false,
        userId: undefined,
        cookieDomain: undefined,
        domains: undefined,
        preInitActions: [],
      });
    }
    app.mount('#app');
    app.provide('root', ref(app._instance));
  } catch (e) {
    // Unmount Loading screen
    loadingApp.unmount();
    console.warn('App initialization failed.');
    console.error(e);

    // Import SFC for fallback app
    const Fallback = defineAsyncComponent(() => import('@/Fallback.vue'));

    // Mount alternative Vue app to display fallback page
    const fallbackApp = createApp(Fallback, { error: e }).use(i18n);
    fallbackApp.mount('#app');
  }
};

const getSentryIntegrations = (router: Router): Array<Integration> => {
  const integrations = new Array<Integration>();
  if (config.SENTRY_TRACING_ACTIVE === true) {
    integrations.push(Sentry.browserTracingIntegration({ router }));
  }
  if (config.SENTRY_REPLAY_ACTIVE === true) {
    integrations.push(Sentry.replayIntegration());
  }
  return integrations;
};

const initSentry = (app, router: Router) => {
  // Add Sentry
  try {
    if (config.SENTRY_ACTIVE === true) {
      Sentry.init({
        app,
        dsn: config.SENTRY_DSN,
        environment: config.SENTRY_ENVIRONMENT,

        integrations: getSentryIntegrations(router),

        // tracePropagationTargets: [
        //   'localhost',
        //   /^https:\/\/knowledge-vm\.local\.molit\.eu/,
        //   /^https:\/\/cats-test\.molit\.eu/,
        //   /^https:\/\/cats\.molit\.eu/,
        // ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  } catch (error) {
    console.error('Sentry could not be initialized');
    console.error(error);
  }
};

initialize();
